// Main SDK entry point - Universal initialization
import { RuntimeEngine } from '@runtime';
import { SDKConfig, EditorConfig, CampaignSDK, CampaignEditor } from '@core/types';
import { SDK_VERSION, DEFAULT_CONFIG } from '@shared/constants';
import { createLogger } from '@core/logger';
import { CampaignError } from '@shared/errors';

// Lazy load Editor module only when needed
let EditorModule: typeof import('@editor').Editor | null = null;

declare global {
  interface Window {
    CampaignSDK?: SDKQueue;
    CampaignSDKInstance?: CampaignSDK;
    CampaignEditorInstance?: CampaignEditor;
    __CAMPAIGN_SDK_VERSION__?: string;
    __CAMPAIGN_DEBUG__?: boolean;
  }
}

type SDKQueue = Array<SDKCommand>;

type SDKCommand = ['init', string, SDKConfig?] | ['applyModifications'];

class CampaignSDKCore {
  private static instance: CampaignSDKCore;
  public runtimeEngine: RuntimeEngine;
  private editorInstance: CampaignEditor | undefined;
  private logger = createLogger();

  private constructor() {
    this.runtimeEngine = new RuntimeEngine();
    // Editor is lazy-loaded only when needed
  }

  static getInstance(): CampaignSDKCore {
    if (!CampaignSDKCore.instance) {
      CampaignSDKCore.instance = new CampaignSDKCore();
    }
    return CampaignSDKCore.instance;
  }

  // Universal initialization method
  init(siteKey: string, config: Partial<SDKConfig> = {}): void {
    // Check for debug mode via query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const debugFromQuery = urlParams.get('debug') === '1' || urlParams.get('debug') === 'true';
    const isEditor = urlParams.get('editor') === '1' || urlParams.get('editor') === 'true';

    const fullConfig: SDKConfig = {
      siteKey,
      debug: debugFromQuery,
      autoApply: true,
      ...config,
    };

    if (isEditor) {
      this.initEditor(fullConfig as EditorConfig);
      return;
    }

    // For non-editor mode, initialization happens via fetchCampaignDataAndApply()
    // which is called from initializeFromQueue()
    this.logger.warn('Direct init() call in non-editor mode. Use queue-based initialization.');
  }

  private async initEditor(config: EditorConfig): Promise<void> {
    // Lazy load Editor module only when needed
    if (!EditorModule) {
      EditorModule = await import('@editor').then((m) => m.Editor);
    }

    if (!('sessionToken' in config)) {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionToken = urlParams.get('es') || urlParams.get('editor_session');

      if (!sessionToken) {
        throw new CampaignError('Editor session token required', 'missing_session_token');
      }

      (config as EditorConfig).sessionToken = sessionToken;
    }

    try {
      if (!EditorModule) {
        throw new CampaignError('Editor module not loaded', 'EDITOR_NOT_LOADED');
      }
      this.editorInstance = new EditorModule();
      this.editorInstance!.init(config as EditorConfig);
      window.CampaignEditorInstance = this.editorInstance;
      this.logger.info('Campaign SDK Editor initialized');
    } catch (error) {
      this.logger.error('Failed to initialize editor', error);
      throw error;
    }
  }

  applyModifications(): void {
    if (this.runtimeEngine) {
      this.runtimeEngine.applyModifications();
    }
  }

  getVersion(): string {
    return SDK_VERSION;
  }

  // Cleanup method
  destroy(): void {
    if (window.CampaignSDKInstance) {
      window.CampaignSDKInstance.destroy?.();
      window.CampaignSDKInstance = undefined;
    }

    if (window.CampaignEditorInstance) {
      window.CampaignEditorInstance.destroy?.();
      window.CampaignEditorInstance = undefined;
    }

    this.editorInstance = undefined;
  }
}

// Initialize SDK from global queue (like Google Analytics)
function initializeFromQueue(): void {
  const sdk = CampaignSDKCore.getInstance();
  const urlParams = new URLSearchParams(window.location.search);
  const isEditor = urlParams.get('editor') === '1' || urlParams.get('editor') === 'true';

  // Process queued commands
  if (Array.isArray(window.CampaignSDK)) {
    const queue = [...window.CampaignSDK];
    window.CampaignSDK = [];

    queue.forEach((command) => {
      try {
        switch (command[0]) {
          case 'init':
            if (isEditor) {
              sdk.init(command[1], command[2]);
            } else {
              // Get site key from command before queue is cleared
              const siteKey = command[1] || 'demo-site';
              fetchCampaignDataAndApply(siteKey);
            }
            break;
          case 'applyModifications':
            if (isEditor) {
              sdk.applyModifications();
            }
            break;
          default:
            console.warn(`[Campaign SDK] Unknown command: ${command[0]}`);
        }
      } catch (error: unknown) {
        console.error(`[Campaign SDK] Error executing command: ${command[0]}`, error);
      }
    });
  }
}

// Auto-initialize when DOM is ready
function autoInitialize(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const isEditor = urlParams.get('editor') === '1' || urlParams.get('editor') === 'true';

  if (isEditor) {
    // Editor mode - process queue and initialize editor
    if (window.CampaignSDK && window.CampaignSDK.length > 0) {
      initializeFromQueue();
    }
  } else {
    // Normal mode - process queue (which will handle runtime mode)
    if (window.CampaignSDK && window.CampaignSDK.length > 0) {
      initializeFromQueue();
    }
  }
}

// Fetch campaign data and apply modifications
function fetchCampaignDataAndApply(siteKey: string): void {
  // Check for debug mode via query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const debugFromQuery = urlParams.get('debug') === '1' || urlParams.get('debug') === 'true';

  // Initialize runtime with async data fetching
  const sdk = CampaignSDKCore.getInstance();
  const runtimeConfig = {
    siteKey,
    debug: debugFromQuery,
    autoApply: true,
    platformOrigin: DEFAULT_CONFIG.platformOrigin,
  };

  sdk.runtimeEngine.initialize(siteKey, runtimeConfig).catch((error: unknown) => {
    console.log('[Campaign SDK] Runtime initialization failed', error);
  });
}

// Setup global queue
if (!window.CampaignSDK) {
  window.CampaignSDK = [];
}

// Export main instance
export { CampaignSDKCore as CampaignSDK };

// Export to global scope for debugging and manual access
(window as any).CampaignSDKCore = CampaignSDKCore;

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  autoInitialize();
}

// Export types for TypeScript users
export type {
  SDKConfig,
  EditorConfig,
  CampaignSDK as CampaignSDKType,
  CampaignEditor,
  Modification,
  Campaign,
} from '@core/types';

export { RuntimeEngine } from '@runtime';
